import { theme } from 'aimo-ui';
import CustomCarousel, { itemProps } from 'components/common/CustomCarousel';
import ParkingContext from 'context/ParkingContext';
import useCurrentLicensePlateFreeTime from 'hooks/useCurrentLicensePlateFreeTime';
import useReadPurchasedPrepaidTickets from 'hooks/useReadPurchasedPrepaidTickets';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import React, { useContext, useMemo, useState } from 'react';
import { Dimensions, ViewProps } from 'react-native';
import { useParkingStore } from 'stores/parkingStore';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import { isValidNote } from 'utils/parkUtils';
import LoyaltyPermitVehicleFreeTimeInfoBox from '../LoyaltyPermitVehicleFreeTimeInfoBox';
import { ImportantZoneNotes } from '../ParkingZoneNotes';
import PermitInfoBox from './PermitInfoBox';
import PrepaidInfoBox from './PrepaidInfoBox';
import PricingInfoBox, { PriceInfoProps } from './PricingInfoBox';
import StreetConfirmationInfoBox from './StreetConfirmationInfoBox';

type ItemProps = {
  fromCarousel?: boolean;
};

const InfoCarouselItem: React.FC<
  itemProps<React.FC<ViewProps & ItemProps>>
> = ({ item: Item }) => {
  return (
    <ColumnWithMargins
      style={[{ height: theme.normalising.widthPixel(90) }]}
      mr={20}
      ml={20}>
      <Item fromCarousel />
    </ColumnWithMargins>
  );
};

type InfoCarouselProps = PriceInfoProps;

const InfoCarousel: React.FC<InfoCarouselProps> = ({
  suggestedDurations,
  currentEndTime,
}) => {
  const {
    parkingZoneResult: { data } = { data: undefined },
    salesOrdersForZoneAndVehicle,
  } = useContext(ParkingContext);
  const { selectedParkingVehiclePlate } = useParkingStore((state) => state);
  const selectedVehicle = useSelectedParkingVehicle(
    false,
    undefined,
    selectedParkingVehiclePlate
  );
  const { countryCode, locationType, notes } = data?.parkZone ?? {};
  const { hasPrepaidForSelectedVehicle } = useReadPurchasedPrepaidTickets({
    selectedVehicle: selectedVehicle,
  });

  const showStreetConfirmation =
    locationType === 'STREET' && countryCode === 'FI';
  const showPermitInfo = (salesOrdersForZoneAndVehicle?.length ?? 0) > 0;
  const showPrepaidForSelectedVehicle = hasPrepaidForSelectedVehicle.length > 0;
  const [showPricingInfoBox, setShowPricingInfoBox] = useState(false);
  const showImportantNotes = notes?.some((note) =>
    isValidNote(note, countryCode, 3)
  );

  const selectedVehicleWithFreeTime = useCurrentLicensePlateFreeTime();
  const showLoyaltyFreeTimeBox = !!selectedVehicleWithFreeTime?.freeTime;

  const carouselData = useMemo(() => {
    return [
      ...(showImportantNotes ? [ImportantZoneNotes] : []),
      ...(showLoyaltyFreeTimeBox ? [LoyaltyPermitVehicleFreeTimeInfoBox] : []),
      ...(showStreetConfirmation ? [StreetConfirmationInfoBox] : []),
      ...(showPrepaidForSelectedVehicle ? [PrepaidInfoBox] : []),
      ...(showPermitInfo ? [PermitInfoBox] : []),
    ];
  }, [
    showImportantNotes,
    showStreetConfirmation,
    showPermitInfo,
    showPrepaidForSelectedVehicle,
    showLoyaltyFreeTimeBox,
  ]);

  return (
    <ColumnWithMargins mt={theme.normalising.heightPixel(15)}>
      <PricingInfoBox
        showPricingCallback={setShowPricingInfoBox}
        suggestedDurations={suggestedDurations}
        disableShadow={carouselData.length === 0}
        currentEndTime={currentEndTime}
      />
      {carouselData.length > 0 && (
        <ColumnWithMargins
          mb={theme.normalising.heightPixel(carouselData.length > 1 ? 15 : 0)}>
          <CustomCarousel
            carouselData={carouselData}
            carouselItem={InfoCarouselItem}
            enabled={!showPricingInfoBox}
            height={theme.normalising.widthPixel(90)}
            width={Dimensions.get('window').width}
          />
        </ColumnWithMargins>
      )}
    </ColumnWithMargins>
  );
};

export default InfoCarousel;
